import { Badge, PersonAdd, SwapVert } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
} from "@mui/material";
import DataList from "components/DataList";
import GenericModal from "components/GenericModal";
import MDTypography from "components/MDTypography";
import SplitPanelSelector from "components/SplitPanelSelector";
import { useAppContextController } from "context/AppContext";
import fetchApplicants from "layouts/pages/applicants/actions/fetchApplicants";
import useJobStatusFilterCards from "layouts/pages/companyjobs/components/CompanyJobsStatus/useJobStatusFilterCards";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";
import InterviewTeamMemberRow from "./InterviewAddApplicantRow";

const sortValues = {
  FIRSTNAME: "firstName:asc",
  RANK: "jobsCopy.score:desc,firstName:asc,_id:asc",
  APPLICATION_DATE: "jobsCopy.dateModified:desc,firstName:asc",
};

const InterviewAddApplicantModal = ({ open, setOpen, jobSlug, onAdd, currentApplicants }) => {
  const { currentLoggedUser } = useAppContextController();
  const { enqueueSnackbar } = useSnackbar();

  const currentApplicantsIndexed = useMemo(() => {
    const result = {};
    if (currentApplicants?.length) {
      currentApplicants.forEach((element) => {
        result[element._id ?? element.id] = element;
      });
    }
    return result;
  }, [currentApplicants]);

  const [applicantDataFilter, setApplicantDataFilter] = useState({
    status: "all",
  });
  const [dataFilter, setDataFilter] = useState({ status: "Applies" });
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(sortValues.RANK);

  const [anchorEl, setAnchorEl] = useState(null);
  const handleIconClick = (e) => setAnchorEl(e.currentTarget);
  const handleIconClose = () => setAnchorEl(null);

  const handleSortByChange = (e) => {
    setSortBy(e.target.value);
    handleIconClose();
  };

  const orderByOverride = sortBy;

  const options = useMemo(() => {
    const result = {
      fetchAll: false,
      orderByOverride,
      limit: 10,
      useElemMatch: true,
    };
    return result;
  }, [orderByOverride]);

  const fetchOptions = useMemo(() => {
    const updatedOptions = {
      ...options,
      filters: {
        "jobs.jobSlug": jobSlug,
      },
      page,
    };
    if (applicantDataFilter.status !== "all")
      updatedOptions.filters = {
        ...updatedOptions.filters,
        "jobs.applicantStatus": applicantDataFilter.status,
      };
    if (dataFilter.status !== "Applies") {
      updatedOptions.filters = {
        ...updatedOptions.filters,
        "jobs.employmentStage": dataFilter.status,
      };
    } else {
      updatedOptions.filters = {
        ...updatedOptions.filters,
        "jobs.employmentStage": "$null$;Submitted;Interview;Waitlist",
      };
    }
    return updatedOptions;
  }, [options, jobSlug, page, applicantDataFilter.status, dataFilter.status]);

  const mapApplicantsAddExtraInfo = (appl) => {
    let rank = 0;
    let aiRank = null;
    let jobModifiedDate = "";
    let jobApplyDate = "";
    let jobAgent = "";
    let jobModifiedAgent = "";
    let jobModifiedAgentName = "";
    let aiScreened = null;
    let waitingForCall = null;
    const foundJob = (appl.jobs ?? []).find((applJob) => applJob.jobSlug === jobSlug);
    if (foundJob) {
      rank = foundJob.score ?? 0;
      jobModifiedDate = foundJob.dateModified ?? "";
      jobApplyDate = foundJob.applyDate ?? "";
      jobAgent = foundJob.agent !== "Internet" && foundJob.agent !== "Indeed" ? foundJob.agent : "";
      jobModifiedAgent = foundJob.modifiedAgent ?? "";
      jobModifiedAgentName = foundJob.modifiedAgentName ?? "";
      aiScreened = foundJob.aiScreened;
      waitingForCall = foundJob.waitingForCall;
      if (foundJob.aiInterview?.score != null) {
        aiRank = foundJob.aiInterview?.score ?? 0;
      }
    }

    return {
      ...appl,
      rank,
      aiRank,
      jobModifiedDate,
      jobApplyDate,
      jobAgent,
      jobModifiedAgent,
      jobModifiedAgentName,
      aiScreened,
      waitingForCall,
    };
  };

  const queryFunction = async ({ queryKey, pageParam }) => {
    let data = await fetchApplicants({ ...fetchOptions, page: pageParam ?? 1 });
    if (data?.data) {
      data = {
        ...data,
        data: data.data.map(mapApplicantsAddExtraInfo),
      };
    }
    return data;
  };

  const {
    data: applicants,
    isLoading,
    isFetching,
    refetch: refetchApplicants,
    fetchNextPage,
  } = useInfiniteQuery(["jobsStatusApplicants", fetchOptions], queryFunction, {
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage?.data?.length || !lastPage?.pagination?.next?.page) {
        return undefined;
      }

      const nextPage = lastPage?.pagination?.next?.page;
      return nextPage ?? false;
    },
    cacheTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const fullData = useMemo(
    () =>
      applicants?.pages?.flatMap((p) => {
        const currentItems = p?.data || [];
        return currentItems;
      }),
    [applicants?.pages]
  );

  const { jobApplicantOptions, jobStatusOptions } = useJobStatusFilterCards({
    currentJob: { jobSlug },
    applicantStatus: applicantDataFilter.status,
    isShowingEmployees: true,
  });

  const jobApplicantDropdownOptions = useMemo(() => {
    return jobApplicantOptions.map((item) => ({
      title: item.title,
      icon: item.icon,
      hasShadow: applicantDataFilter.status === item.value ? "emd" : "",
      color: item.iconColor,
      ariaLabel: item.title,
      fn: () => setApplicantDataFilter((prevState) => ({ ...prevState, status: item.value })),
      name: item.value,
      selectedTitle: item.value === "all" ? "Applicant Filter" : null,
      selectedIcon: item.value === "all" ? <PersonAdd fontSize="small" color="warning" /> : null,
      badgeCount: 0,
    }));
  }, [applicantDataFilter.status, jobApplicantOptions]);

  const jobStatusDropdownOptions = useMemo(() => {
    return jobStatusOptions
      .filter((item) => ["Applies", "Submitted", "Interview", "Waitlist"].includes(item.value))
      .map((item) => ({
        title: item.title,
        icon: item.icon,
        hasShadow: dataFilter.status === item.value ? "emd" : "",
        color: item.iconColor,
        ariaLabel: item.title,
        fn: () => setDataFilter((prevState) => ({ ...prevState, status: item.value })),
        name: item.value,
        selectedTitle: item.value === "Applies" ? "Job Filter" : null,
        selectedIcon: item.value === "Applies" ? <Badge fontSize="small" color="success" /> : null,
        badgeCount: 0,
      }));
  }, [dataFilter.status, jobStatusOptions]);

  const header = (
    <MDTypography variant="h5" color="dark" sx={{ mt: 2, mb: 1, ml: 2 }}>
      Interview Available Candidates
    </MDTypography>
  );

  const modalBody = (
    <Box>
      <Grid container rowSpacing={2} sx={{ opacity: 1, gap: 2, pb: 4 }}>
        <SplitPanelSelector
          panelOptions={jobApplicantDropdownOptions}
          selectedPanel={applicantDataFilter?.status}
          isEdit
        />
        <SplitPanelSelector
          panelOptions={jobStatusDropdownOptions}
          selectedPanel={dataFilter?.status}
          isEdit
        />
        <Grid item flexGrow={1} />
        <Button
          variant="text"
          startIcon={<SwapVert />}
          onClick={handleIconClick}
          // disabled={isSubmitting}
          style={{ fontSize: 14 }}
        >
          Sort By
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleIconClose}
          sx={{
            "& .MuiPaper-root": { minWidth: "10rem", p: "0.2rem" },
            li: { minWidth: "10rem", pl: "0.5rem", pr: "0.5rem" },
          }}
        >
          <RadioGroup
            aria-labelledby={`sortbyradiobuttons_${jobSlug}`}
            value={sortBy}
            onChange={handleSortByChange}
            name="radio-buttons-group"
            sx={{ p: "0.2rem" }}
          >
            <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
              <FormControlLabel value={sortValues.FIRSTNAME} control={<Radio />} label="Name" />
            </MenuItem>
            <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
              <FormControlLabel value={sortValues.RANK} control={<Radio />} label="Rank" />
            </MenuItem>
            <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
              <FormControlLabel
                value={sortValues.APPLICATION_DATE}
                control={<Radio />}
                label={dataFilter.status === "Interview" ? "Interview Date" : "Last Activity Date"}
              />
            </MenuItem>
          </RadioGroup>
        </Menu>
      </Grid>
      <DataList
        renderRow={(row, idx) => (
          <InterviewTeamMemberRow
            row={row}
            idx={idx}
            onAdd={onAdd}
            jobSlug={jobSlug}
            disabled={!!currentApplicantsIndexed[row._id ?? row.id]}
          />
        )}
        data={fullData ?? []}
        greybar
        divider
        scrollY
        tableHeight={450}
        height={500}
        isLoading={isLoading}
        noPadding
        isFetching={isFetching}
        loadMore={fetchNextPage}
        infiniteData={applicants?.pages?.[applicants?.pages?.length - 1]}
      />
    </Box>
  );

  const buttons = (
    <>
      <Box display="flex" flexDirection="column" sx={{ paddingRight: "1rem" }}>
        <Button
          variant="contained"
          style={{ color: "white" }}
          onClick={(e) => {
            setOpen(false);
          }}
        >
          Close
        </Button>
      </Box>
    </>
  );

  return (
    <>
      <GenericModal
        open={open}
        setOpen={setOpen}
        body={modalBody}
        header={header}
        buttons={buttons}
        sx={(theme) => ({
          width: "90% !important",
          maxWidth: "1100px !important",
          // paddingBottom: "10px",
          [theme.breakpoints.down("md")]: {
            "& .MuiCardContent-root": {
              padding: "0px",
            },
          },
        })}
      />
    </>
  );
};

export default InterviewAddApplicantModal;
